import { useMemo } from "react";

import { useQuery } from "@apollo/client";

import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { CofConfigId, type MyLensesRole } from "../../../gql-generated/types";
import { type GetRolesQuery, type GetRolesQueryVariables, getRoles } from "../../api/queries";
import { type ApolloResult } from "../../models";

export const makeContainerKey = (containerId: string, containerKind: string): string =>
    `${containerKind}:${containerId}`;

export const makeRolesStore = (roles: readonly MyLensesRole[]): Record<string, MyLensesRole[]> => {
    const rolesStore: Record<string, MyLensesRole[]> = {};

    for (const role of roles) {
        const { containerId, containerKind } = role;
        const key = makeContainerKey(containerId, containerKind);
        if (!(key in rolesStore)) {
            rolesStore[key] = [];
        }
        rolesStore[key]?.push(role);
    }

    return rolesStore;
};

export const useRolesStore = (): ApolloResult<{ rolesStore: Record<string, MyLensesRole[]> | null }> => {
    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const { data, loading, error } = useQuery<GetRolesQuery, GetRolesQueryVariables>(getRoles, {
        skip: isMyLensesOrgsV2Enabled,
    });

    return useMemo(() => {
        if (!data) {
            return {
                rolesStore: null,
                loading,
                error,
            };
        }
        return {
            rolesStore: makeRolesStore(data.getMyLensesRoles.rolesList),
            loading,
            error,
        };
    }, [data, loading, error]);
};
